import { t } from 'i18next';
import {
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import { SidebarLeftCloseIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, tokens } from '@treams/treams-ui';

export const CONTEXT_SIDEBAR_WIDTH = 320;

const LayoutFooterContext = createContext<HTMLElement | null>(null);

export function LayoutFooter(props: PropsWithChildren) {
	const slot = useContext(LayoutFooterContext);

	if (!slot) {
		return <>{props.children}</>;
	}

	return createPortal(props.children, slot);
}

const Page = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	height: 100%;
	overflow: hidden;

	@media print {
		display: block;
		height: auto !important;
		overflow: visible !important;
	}
`;

const Body = styled.div`
	position: relative;
	display: flex;
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow: hidden;

	@media print {
		display: block;
		height: auto !important;
		overflow: visible !important;
		min-height: 0;
	}
`;

const Scroll = styled.div`
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow: auto;

	@media print {
		height: auto !important;
		overflow: visible !important;
		min-height: 0;
	}
`;

const Slot = styled.div`
	width: ${CONTEXT_SIDEBAR_WIDTH}px;
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	z-index: 3;
	pointer-events: none;
	overflow-x: clip;

	@media print {
		display: none;
	}
`;

const Panel = styled.div<{ $open?: boolean }>`
	position: relative;
	z-index: 3;
	width: ${CONTEXT_SIDEBAR_WIDTH}px;
	height: 100%;
	max-width: 100%;
	background: ${tokens.surface.secondary};
	border-left: 1px solid ${tokens.border.subtle};
	overflow: hidden;
	transition: 1s cubic-bezier(0, 1, 0, 1) transform;
	transform: ${(props) => (props.$open ? 'none' : 'translateX(100%)')};
	pointer-events: auto;
`;

const OpenButton = styled.div`
	position: absolute;
	top: ${tokens.padding.container}px;
	right: ${tokens.padding.container}px;
	z-index: 2;
	pointer-events: auto;
`;

const FooterSlot = styled.div`
	flex-shrink: 0;
	width: 100%;

	&:empty {
		display: none;
	}

	@media print {
		display: none;
	}
`;

export type ContextSidebarLayoutProps = PropsWithChildren<{
	isSideBarOpen: boolean;
	onSidebarOpen: (open: boolean) => void;
	renderSidebar?: (props: { onClose: () => void }) => ReactNode;
}>;

export default function ContextSidebarLayout(props: ContextSidebarLayoutProps) {
	const [isSidebarOpen, setIsSidebarOpen] = useState(props.isSideBarOpen);
	const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
	const hasContextSidebar = useFeature(graphql.FeatureName.EvaluationsContext);
	const showSidebar = hasContextSidebar && !!props.renderSidebar;

	const { onSidebarOpen } = props;

	useEffect(() => {
		onSidebarOpen(isSidebarOpen);
	}, [isSidebarOpen, onSidebarOpen]);

	return (
		<LayoutFooterContext.Provider value={footerSlot}>
			<Page>
				<Body>
					<Scroll>{props.children}</Scroll>
					{showSidebar && (
						<Slot>
							<OpenButton>
								<Button
									type="secondary"
									icon={<SidebarLeftCloseIcon />}
									title={t('conversation.sidebar.context.openButton.title')}
									onClick={() => setIsSidebarOpen(true)}
								/>
							</OpenButton>
							<Panel $open={isSidebarOpen}>
								{props.renderSidebar?.({
									onClose: () => setIsSidebarOpen(false),
								})}
							</Panel>
						</Slot>
					)}
				</Body>
				<FooterSlot ref={setFooterSlot} />
			</Page>
		</LayoutFooterContext.Provider>
	);
}
